import { View, StyleProp, ViewStyle, TouchableOpacity, Text, StyleSheet } from 'react-native'
import { openBrowserAsync } from 'expo-web-browser'
import { fontFamilies, mesaColorNames, typography } from '@/src/common/theme'
import * as Branding from '@/src/experiences/branding'
import { useMemo } from 'react'

export type CardOrderStatus = 'SHIPPED' | 'PRINTED' | 'SUBMITTED' | null | undefined

interface CardDeliveryStatusProps {
  status?: CardOrderStatus
  style?: StyleProp<ViewStyle>
  trackingNumber?: string | null
}

export function CardDeliveryStatus({ status, style, trackingNumber }: CardDeliveryStatusProps) {
  // Shipped cards must be printed, and printed cards must be ordered
  const isShipped = status === 'SHIPPED'
  const isPrinted = status === 'PRINTED'
  const isOrdered = status === 'SUBMITTED'
  const { colorValue: primaryColor } = Branding.Hooks.usePrimaryColor()
  const styles = useMemo(() => makeStyles(primaryColor), [primaryColor])

  const handleTrackShipment = () => {
    void openBrowserAsync(
      `https://tools.usps.com/go/TrackConfirmAction_input?strOrigTrackNum=${trackingNumber}`,
    )
  }

  return (
    <View style={style}>
      <View style={styles.headerContainer}>
        <Text style={styles.cardDeliveryTitle}>Card Delivery Status</Text>
        {isShipped && trackingNumber && (
          <TouchableOpacity onPress={handleTrackShipment}>
            <Text style={styles.trackShipmentText}>Track Shipment</Text>
          </TouchableOpacity>
        )}
      </View>
      <View style={styles.progressContainer}>
        <View
          style={
            isOrdered || isPrinted || isShipped
              ? styles.progressBarCompleted
              : styles.progressBarPending
          }
        />
        <View
          style={isPrinted || isShipped ? styles.progressBarCompleted : styles.progressBarPending}
        />
        <View style={isShipped ? styles.progressBarCompleted : styles.progressBarPending} />
        <View style={styles.dividerContainer}>
          <View style={styles.divider} />
          <View style={styles.divider} />
        </View>
      </View>
      <View style={styles.statusLabelsContainer}>
        <Text style={isOrdered ? styles.statusLabelCompleted : styles.statusLabelPending}>
          Ordered
        </Text>
        <Text style={isPrinted ? styles.statusLabelCompleted : styles.statusLabelPending}>
          Printed
        </Text>
        <Text style={isShipped ? styles.statusLabelCompleted : styles.statusLabelPending}>
          Shipped
        </Text>
      </View>
    </View>
  )
}

const makeStyles = (primaryColor: string) =>
  StyleSheet.create({
    headerContainer: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    cardDeliveryTitle: {
      fontFamily: fontFamilies['PlusJakartaSans-Bold'],
      fontWeight: 700,
      fontSize: 14,
      color: mesaColorNames['Greyscale/900 #152438'],
    },
    trackShipmentText: {
      fontFamily: fontFamilies['PlusJakartaSans-Bold'],
      fontWeight: 700,
      fontSize: 14,
      color: primaryColor,
    },
    progressContainer: {
      position: 'relative',
      height: 12,
      flexDirection: 'row',
      borderRadius: 5,
      overflow: 'hidden',
      marginTop: 12,
    },
    progressBarCompleted: {
      flex: 1,
      backgroundColor: '#3d8c5f',
    },
    progressBarPending: {
      flex: 1,
      backgroundColor: '#6e6e73',
    },
    dividerContainer: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      flexDirection: 'row',
      justifyContent: 'space-between',
      paddingHorizontal: '33%',
      pointerEvents: 'none',
    },
    divider: {
      width: 2,
      height: '100%',
      backgroundColor: 'white',
      transform: [{ skewX: '-30deg' }],
    },
    statusLabelsContainer: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginTop: 8,
    },
    statusLabelCompleted: {
      ...typography['label/xs-bold'],
      color: mesaColorNames['Greyscale/900 #152438'],
    },
    statusLabelPending: {
      ...typography['label/xs-regular'],
      color: mesaColorNames['Greyscale/600 #505D64'],
    },
  })
